/**
 * Approval chains a person can create with one click.
 *
 * Every step is held by a ROLE (reviewer, approver, administrator), because
 * that is how the product decides who may act; the step's NAME says whose
 * turn it is. The "basis" lines quote only what the federal Conditions of
 * Participation say (42 CFR 482 hospitals, 483 nursing homes, 484 home
 * health), read 2026-09-19. They say who is responsible; no rule prescribes
 * a chain, so these are common shapes, not requirements.
 */

import type { Role } from '../../types/roles';

export type StepRole = Extract<Role, 'reviewer' | 'approver' | 'admin'>;

export interface WorkflowExample {
  key: string;
  name: string;
  /** Who this example is drawn from. Never a claim that a chain is required. */
  suits: string;
  description: string;
  /**
   * `isCommittee` is the only setting a ready-made step carries, and it is set
   * only where the step's NAME is an organized body that meets: approving such
   * a step records the date it met, which is the evidence a reviewer asks for.
   * A step that could reasonably be one person signing is left ordinary, so
   * nobody is made to invent a meeting date. Everything else about a step is
   * chosen afterward, under Edit.
   */
  steps: { name: string; roleId: StepRole; isCommittee?: boolean }[];
  /** What the regulation says, where one speaks to it. */
  basis?: string;
  /**
   * Standard document type names this chain is for. Created covering the ones
   * the organization has under those names; none, and it covers nothing until
   * somebody says what it is for.
   */
  forTypes?: string[];
}

export const WORKFLOW_EXAMPLES: WorkflowExample[] = [
  {
    key: 'single',
    name: 'One approver',
    suits: 'Example for a clinic or small practice where one person signs off.',
    description: 'One step. Anyone holding the Approver role can approve, and the policy is published.',
    steps: [{ name: 'Approval', roleId: 'approver' }],
  },
  {
    key: 'manager_medical_director',
    name: 'Manager, then medical director',
    suits: 'Example for a physician practice, ambulatory surgery center or urgent care.',
    description: 'The practice manager checks that it is complete and workable; the medical director approves it.',
    steps: [
      { name: 'Practice manager review', roleId: 'reviewer' },
      { name: 'Medical director approval', roleId: 'approver' },
    ],
  },
  {
    key: 'director_committee',
    name: 'Director, then policy committee',
    suits: 'Example chain for a community or critical access hospital.',
    description: 'The department director reads it first, so the committee only sees policies a leader already stands behind.',
    steps: [
      { name: 'Department director review', roleId: 'reviewer' },
      { name: 'Policy committee approval', roleId: 'approver', isCommittee: true },
    ],
  },
  {
    key: 'clinical_chain',
    name: 'Clinical chain to the medical staff',
    suits: 'Example for a hospital whose patient care policies go to the Medical Executive Committee.',
    description: 'Department, then nursing leadership, then the medical staff.',
    steps: [
      { name: 'Department director review', roleId: 'reviewer' },
      { name: 'Chief Nursing Officer review', roleId: 'reviewer' },
      { name: 'Medical Executive Committee approval', roleId: 'approver', isCommittee: true },
    ],
    basis: '42 CFR 482.25 makes the medical staff responsible for policies that minimize drug errors; 482.23 has the director of nursing approve nursing policies for outpatient departments.',
  },
  {
    key: 'governing_body',
    name: 'Full chain to the governing body',
    suits: 'Example for a hospital whose board gives the final approval.',
    description: 'Four stops. An administrator records the last step once the board has voted.',
    steps: [
      { name: 'Department director review', roleId: 'reviewer' },
      { name: 'Policy committee review', roleId: 'reviewer', isCommittee: true },
      { name: 'Medical Executive Committee approval', roleId: 'approver', isCommittee: true },
      { name: 'Governing body approval', roleId: 'admin', isCommittee: true },
    ],
    basis: '42 CFR 482.22 requires medical staff bylaws to be approved by the governing body.',
  },
  {
    key: 'nursing_home',
    name: 'Nursing home',
    suits: 'Example for a skilled nursing or long-term care facility.',
    description: 'The Director of Nursing and the medical director read it; the administrator approves for the governing body.',
    steps: [
      { name: 'Director of Nursing review', roleId: 'reviewer' },
      { name: 'Medical director review', roleId: 'reviewer' },
      { name: 'Administrator approval', roleId: 'approver' },
    ],
    basis: '42 CFR 483.70 makes the governing body legally responsible for the facility’s policies and the medical director responsible for implementing resident care policies.',
  },
  {
    key: 'home_health',
    name: 'Home health agency',
    suits: 'Example for a home health or hospice agency.',
    description: 'The clinical manager, who oversees patient care, reads it; the administrator approves.',
    steps: [
      { name: 'Clinical manager review', roleId: 'reviewer' },
      { name: 'Administrator approval', roleId: 'approver' },
    ],
    basis: '42 CFR 484.105 gives the governing body full legal authority, the administrator day-to-day operations, and the clinical manager oversight of patient care.',
  },
  {
    key: 'protocols_order_sets',
    name: 'Clinical protocols and order sets',
    suits: 'Hospitals. For Clinical Protocol and Protocol documents only.',
    description: 'The three sign-offs the federal rule names for standing orders, order sets and protocols, then the medical staff.',
    steps: [
      { name: 'Nursing leadership review', roleId: 'reviewer' },
      { name: 'Pharmacy leadership review', roleId: 'reviewer' },
      { name: 'Medical staff approval', roleId: 'approver' },
    ],
    basis: '42 CFR 482.24 requires standing orders, order sets and protocols to be reviewed and approved by the medical staff and by nursing and pharmacy leadership.',
    forTypes: ['Clinical Protocol', 'Protocol'],
  },
  {
    key: 'forms_procedures',
    name: 'Forms and department procedures',
    suits: 'Any organization. For Form, Procedure and Standard Operating Procedure documents only.',
    description: 'Work that stays inside one department ends with its director, so committees only see what needs them.',
    steps: [{ name: 'Department director approval', roleId: 'approver' }],
    forTypes: ['Form', 'Procedure', 'Standard Operating Procedure'],
  },
  {
    key: 'emergency_plan',
    name: 'Emergency operations plan',
    suits: 'Any provider under the emergency preparedness rule. For Emergency Operations Plan documents only.',
    description: 'The emergency management lead reads it; the administrator approves.',
    steps: [
      { name: 'Emergency management review', roleId: 'reviewer' },
      { name: 'Administrator approval', roleId: 'approver' },
    ],
    basis: '42 CFR 482.15 requires the emergency plan and its policies to be reviewed and updated at least every 2 years.',
    forTypes: ['Emergency Operations Plan'],
  },
];
